import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { useForm } from "react-hook-form";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { toast } from "react-toastify";
import moment from "moment";
import { safeText } from "../utils/hubHelpers";

export default function EditMachineryModal({ isOpen, toggle, machine, onSuccess }) {
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchSitesAndVendors();
      if (machine) {
        populateForm(machine);
      }
    }
  }, [isOpen, machine]);

  const fetchSitesAndVendors = async () => {
    try {
      const [siteRes, vendorRes] = await Promise.all([
        callApi(apiUrl.get_site, { method: "GET" }),
        callApi(apiUrl.get_vendor_list, { method: "GET" }),
      ]);

      if (siteRes?.status === true) {
        const list = Array.isArray(siteRes?.data?.site)
          ? siteRes.data.site
          : Array.isArray(siteRes?.data)
          ? siteRes.data
          : [];
        setSites(list);
      }
      if (vendorRes?.status === true) {
        const list = Array.isArray(vendorRes?.data?.vendor)
          ? vendorRes.data.vendor
          : Array.isArray(vendorRes?.data)
          ? vendorRes.data
          : [];
        setVendors(list);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const populateForm = (m) => {
    setValue("name", m?.name || "");
    setValue("assetNo", m?.assetNo || "");
    setValue("type", m?.type || "Machine");
    setValue("fuelType", m?.fuelType || "Diesel");
    setValue("machineNumber", m?.machineNumber || "");
    setValue("readingType", m?.readingType || "Hours");
    setValue("chesisNumber", m?.chesisNumber || "");
    setValue("engineNumber", m?.engineNumber || "");
    setValue("model", m?.model || "");
    setValue("make", m?.make || "");
    setValue("year", m?.year || "");
    setValue("purchaseCost", m?.purchaseCost || "");
    setValue("purchaseDate", m?.purchaseDate ? moment(m.purchaseDate).format("YYYY-MM-DD") : "");
    setValue("poNumber", m?.poNumber || "");
    setValue("ptNumber", m?.ptNumber || "");
    setValue("siteId", m?.siteId || m?.Site?.id || "");
    setValue("vendorId", m?.vendorId || m?.Vendor?.id || "");
    setValue("nextServiceDate", m?.nextServiceDate ? moment(m.nextServiceDate).format("YYYY-MM-DD") : "");
    setValue("status", m?.status || "Operational");
  };

  const onSubmit = async (data) => {
    if (!machine?.id) return;
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        assetNo: data.assetNo || "",
        type: data.type || "Machine",
        fuelType: data.fuelType || "Diesel",
        machineNumber: data.machineNumber || "",
        readingType: data.readingType || "Hours",
        chesisNumber: data.chesisNumber || "",
        engineNumber: data.engineNumber || "",
        model: data.model || "",
        make: data.make || "",
        year: data.year ? parseInt(data.year) : undefined,
        purchaseCost: data.purchaseCost ? parseFloat(data.purchaseCost) : 0,
        purchaseDate: data.purchaseDate || undefined,
        poNumber: data.poNumber || "",
        ptNumber: data.ptNumber || "",
        siteId: data.siteId || undefined,
        vendorId: data.vendorId || undefined,
        nextServiceDate: data.nextServiceDate || undefined,
        status: data.status || "Operational",
      };

      const res = await callApi(`${apiUrl.update_machinery}?machineryId=${machine.id}`, {
        method: "POST",
        data: payload,
      });

      if (res?.status === true) {
        toast.success("Machinery details updated successfully!");
        toggle();
        onSuccess?.();
      } else {
        toast.error(res?.message || "Failed to update machinery");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          toggle={toggle}
          close={
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={toggle}
              aria-label="Close"
              style={{ filter: "invert(1) grayscale(100%) brightness(200%)", opacity: 0.9, cursor: "pointer" }}
            />
          }
          style={{ background: "#0f172a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          ✏️ Edit Machinery: {safeText(machine?.name, "Equipment")} ({safeText(machine?.assetNo, "N/A")})
        </ModalHeader>
        <ModalBody>
          <div className="row g-3">
            <div className="col-12">
              <h6 style={{ fontWeight: 700, color: "#0f172a", borderBottom: "1px solid #e2e8f0", paddingBottom: "4px", marginBottom: "8px" }}>
                1. Basic Equipment Identity
              </h6>
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Machine / Equipment Name *
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. JCB 3DX Backhoe, Diesel Generator 125kVA"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && <small className="text-danger">{errors.name.message}</small>}
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Asset Code / Tag #
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. MCH-001"
                {...register("assetNo")}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Registration / Plate #
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. RJ07-GB-4412"
                {...register("machineNumber")}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Category Type
              </label>
              <select className="form-select" {...register("type")}>
                <option value="Machine">Machine / Heavy Equipment</option>
                <option value="Vehicle">Vehicle / Transport</option>
                <option value="Generator">Generator / Power Unit</option>
                <option value="Lifting">Crane / Boom Lift</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Fuel Type
              </label>
              <select className="form-select" {...register("fuelType")}>
                <option value="Diesel">Diesel</option>
                <option value="Petrol">Petrol</option>
                <option value="Electric">Electric</option>
                <option value="CNG">CNG</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Reading Meter Type
              </label>
              <select className="form-select" {...register("readingType")}>
                <option value="Hours">Hours (Hour meter)</option>
                <option value="KM">Kilometers (Odometer)</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Operational Status
              </label>
              <select className="form-select" {...register("status")}>
                <option value="Operational">Operational</option>
                <option value="Breakdown">Breakdown</option>
                <option value="Under Service">Under Service</option>
                <option value="Idle">Idle</option>
              </select>
            </div>

            <div className="col-12 mt-3">
              <h6 style={{ fontWeight: 700, color: "#0f172a", borderBottom: "1px solid #e2e8f0", paddingBottom: "4px", marginBottom: "8px" }}>
                2. Site Allocation & Technical Details
              </h6>
            </div>

            <div className="col-md-4">
              <label className="form-label">
                Deployment Site
              </label>
              <select className="form-select" {...register("siteId")}>
                <option value="">Select Project / Solar Site</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {safeText(s.name, "Site")}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">
                Make / Manufacturer
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. JCB, Tata, Kirloskar"
                {...register("make")}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">
                Model / Year
              </label>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. 3DX Super"
                  {...register("model")}
                />
                <input
                  type="number"
                  className="form-control"
                  placeholder="2023"
                  style={{ width: 90 }}
                  {...register("year")}
                />
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Chassis Number
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Chassis / Frame #"
                {...register("chesisNumber")}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Engine Number
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Engine Serial #"
                {...register("engineNumber")}
              />
            </div>

            <div className="col-12 mt-3">
              <h6 style={{ fontWeight: 700, color: "#0f172a", borderBottom: "1px solid #e2e8f0", paddingBottom: "4px", marginBottom: "8px" }}>
                3. Procurement & Service Schedule
              </h6>
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Purchase Cost (₹)
              </label>
              <input
                type="number"
                step="any"
                className="form-control"
                placeholder="e.g. 3200000"
                {...register("purchaseCost")}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Purchase Date
              </label>
              <input
                type="date"
                className="form-control"
                {...register("purchaseDate")}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">
                PO Reference
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. PO/24-25/0061"
                {...register("poNumber")}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">
                PT Reference
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. PT/24-25/0061"
                {...register("ptNumber")}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Vendor / Supplier
              </label>
              <select className="form-select" {...register("vendorId")}>
                <option value="">Select Vendor (Optional)</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {safeText(v.firmName || v.name, "Vendor")}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Next Service Date
              </label>
              <input
                type="date"
                className="form-control"
                {...register("nextServiceDate")}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={loading} style={{ background: "#2563eb", borderColor: "#2563eb" }}>
            {loading ? "Saving Changes..." : "Save Changes"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
